import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Trash2, Maximize2 } from 'lucide-react';

const BACKEND_URL = '';
const KELI_AVATAR = "https://customer-assets.emergentagent.com/job_6b14885d-4e67-4026-9228-6b4d6c997648/artifacts/4d19x1t1_Sustainability%20leader%20in%20digital%20innovation.png";

const QUICK_PROMPTS = [
  "What's on this week?",
  "Who should I connect with?",
  "Recommend a course for me",
  "What articles should I read?",
  "What events are coming up?",
  "How can I improve my profile?",
];

/**
 * Render an assistant message body with inline markdown-style links and
 * line breaks. Internal links (starting with `/`) navigate via react-router
 * so the bubble stays open and we don't reload the SPA; external links open
 * in a new tab.
 *
 * Supported tokens (best-effort, no full markdown parser):
 *   [label](url)  — clickable link
 *   plain newlines — preserved as <br/>
 */
function MessageBody({ text, onInternalNav }) {
  if (!text) return null;
  // Tokenise into [link] and plain segments. Regex must be anchored on the
  // full `[text](url)` shape — anything that doesn't match is plain text.
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: 'text', value: text.slice(last, m.index) });
    parts.push({ kind: 'link', label: m[1], url: m[2] });
    last = re.lastIndex;
  }
  if (last < text.length) parts.push({ kind: 'text', value: text.slice(last) });

  const renderText = (s, keyPrefix) =>
    s.split('\n').map((line, i, arr) => (
      <React.Fragment key={`${keyPrefix}-${i}`}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

  return (
    <p className="whitespace-pre-wrap">
      {parts.map((p, idx) => {
        if (p.kind === 'text') return <React.Fragment key={idx}>{renderText(p.value, `t${idx}`)}</React.Fragment>;
        const isInternal = p.url.startsWith('/');
        if (isInternal) {
          return (
            <a
              key={idx}
              href={p.url}
              onClick={(e) => { e.preventDefault(); onInternalNav?.(p.url); }}
              className="text-brand-navy font-medium underline underline-offset-2 hover:text-brand-dark"
              data-testid={`keli-link-${p.url}`}
            >
              {p.label}
            </a>
          );
        }
        return (
          <a
            key={idx}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-navy font-medium underline underline-offset-2 hover:text-brand-dark"
          >
            {p.label}
          </a>
        );
      })}
    </p>
  );
}

export default function KellyBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEnd = useRef(null);
  const navigate = useNavigate();

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    if (open && !historyLoaded) {
      fetch(`${BACKEND_URL}/api/assistant/history`, { credentials: 'include', headers })
        .then(r => r.json())
        .then(data => { if (Array.isArray(data)) setMessages(data); setHistoryLoaded(true); })
        .catch(() => setHistoryLoaded(true));
    }
  }, [open]);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const res = await fetch(`${BACKEND_URL}/api/assistant/chat`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ message: msg, timezone: tz }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Please try again!", timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    await fetch(`${BACKEND_URL}/api/assistant/history`, { method: 'DELETE', credentials: 'include', headers });
    setMessages([]);
  };

  return (
    <>
      {/* Bubble button */}
      {!open && (
        <button data-testid="kelly-bubble-btn" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105 overflow-hidden ring-2 ring-brand-blue ring-offset-2">
          <img src={KELI_AVATAR} alt="Kelly" className="w-full h-full object-cover object-[50%_20%]" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div data-testid="kelly-chat-panel" className="fixed bottom-6 right-6 z-50 w-[380px] h-[540px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-dark via-brand-dark to-brand-navy px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src={KELI_AVATAR} alt="Kelly" className="w-10 h-10 rounded-full object-cover ring-2 ring-brand-blue" />
              <div>
                <h3 className="text-white font-bold text-sm">Keli</h3>
                <p className="text-brand-blue/60 text-xs">Your SLL Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setOpen(false); navigate('/assistant'); }}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Full screen">
                <Maximize2 className="h-4 w-4" />
              </button>
              <button onClick={clearHistory}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Clear history">
                <Trash2 className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && !loading && (
              <div className="text-center py-6">
                <img src={KELI_AVATAR} alt="Kelly" className="w-16 h-16 rounded-full mx-auto mb-3 ring-2 ring-brand-blue/40 object-cover object-[50%_20%]" />
                <p className="text-sm font-semibold text-slate-700 mb-1">Hi! I'm Keli</p>
                <p className="text-xs text-slate-500 mb-4">Your personal SLL assistant. I can recommend connections, courses, articles and more based on your profile.</p>
                <div className="space-y-2">
                  {QUICK_PROMPTS.map((p, i) => (
                    <button key={i} onClick={() => sendMessage(p)}
                      className="block w-full text-left px-3 py-2 text-xs bg-white border border-slate-200 rounded-lg hover:border-brand-blue hover:bg-brand-navy/5 transition-all text-slate-600">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                {m.role === 'assistant' && (
                  <img src={KELI_AVATAR} alt="Kelly" className="w-7 h-7 rounded-full flex-shrink-0 mt-1 object-cover object-[50%_20%]" />
                )}
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-brand-navy text-white rounded-br-sm'
                    : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                }`}>
                  {m.role === 'assistant' ? (
                    <MessageBody
                      text={m.content}
                      onInternalNav={(url) => { setOpen(false); navigate(url); }}
                    />
                  ) : (
                    <p className="whitespace-pre-line">{m.content}</p>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2">
                <img src={KELI_AVATAR} alt="Kelly" className="w-7 h-7 rounded-full flex-shrink-0 object-cover object-[50%_20%]" />
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white flex-shrink-0">
            <div className="flex gap-2">
              <input data-testid="kelly-chat-input" type="text" placeholder="Ask Keli anything..."
                value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                disabled={loading} />
              <button data-testid="kelly-send-btn" onClick={() => sendMessage()} disabled={loading || !input.trim()}
                className="bg-brand-navy hover:bg-brand-dark text-white rounded-xl px-3 py-2.5 disabled:opacity-40 transition-colors">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
